import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box"
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";

import planVersionService from "@plannel/services/data/plan-version-service";
import VersionConfigContent from "@plannel/components/VersionConfigContent";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import runRpService from "@plannel/services/rp/run-rp-service";

const DIALOG_TITLE = { OTHER_FIELD_CHANGED: "OTHER_FIELD_CHANGED", ONLY_DPVERSION_CHANGED: "ONLY_DPVERSION_CHANGED", MULTI_SOURCING_CHANGED: "MULTI_SOURCING_CHANGED" };
const { RP, MP } = CodeUtil.module;
const { RELEASED } = CodeUtil.versionStatusCode;
const { snackBarMessageColor } = ColorUtil;
const PANEL_WIDTH = { MP: 520, RP: 560 };
const VersionSettingsModal = ({ t, viewName, moduleCd, modalOpen, setModalOpen, defaultValue, selectedPlanVersion, dpVersions, rpVersions, retrievePlanVersion, setSnackInfo, setProgressOn, useModules }) => {
  const reduxDispatch = useDispatch();
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [expanded, setExpanded] = useState(true);
  const configObj = useRef({});
  const [planPolicyEl, setPlanPolicyEl] = useState(null);
  const [rpPolicyEl, setRpPolicyEl] = useState(null);

  const isMp = moduleCd === MP;
  const isRp = moduleCd === RP;

  const psiUpdate = () => {
    selectedPlanVersion.planConfigObj = configObj.current;

    setModalOpen(false);
    setProgressOn(true);

    runRpService.psiUpdate(selectedPlanVersion)
      .then((response) => {
        const planVersion = response.data;
        if (planVersion && !isEmpty(planVersion)) {
          retrievePlanVersion(planVersion);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        }
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: selectedPlanVersion.name }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }

        debug(e);
      })
      .finally(() => {
        setProgressOn(false);
      });
  }

  const planVersionUpsert = () => {
    selectedPlanVersion.execTs = null;
    selectedPlanVersion.planConfigObj = configObj.current;

    setModalOpen(false);
    setProgressOn(true);

    planVersionService.upsert(selectedPlanVersion)
      .then((response) => {
        const planVersion = response.data;
        if (planVersion && !isEmpty(planVersion)) {
          retrievePlanVersion(planVersion);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        }
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: selectedPlanVersion.name }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }

        debug(e);
      })
      .finally(() => {
        setProgressOn(false);
      });

  }

  const handleDialog = (action, falseAction) => {
    switch (action) {
      case "PSI_UPDATE": {
        psiUpdate();
        break;
      }
      case "VERSION_CONFIG_UPDATE": {
        planVersionUpsert();
        break;
      }
      case false: {
        if (falseAction === "RPPSIUPDATE") {
          planVersionUpsert();
        }
        break;
      }
      default:
        break;
    }
  }

  const handleDialogOpen = (action) => {
    if (action === DIALOG_TITLE.ONLY_DPVERSION_CHANGED) {
      setDialogInfo({ open: true, title: "rpPsiUpdate", actionName: "PSI_UPDATE", content: `${t("MSG_ConfirmRpPsiUpdate")}\n`, cancelText: `${t("MSG_DeletePreviousResult")}`, useCustomButton: true });
    } else if (action === DIALOG_TITLE.MULTI_SOURCING_CHANGED) {
      setDialogInfo({ open: true, title: "update", actionName: "VERSION_CONFIG_UPDATE", content: `${t("MSG_ConfirmChangeMultiSourcingOption")}\n`, useCustomButton : false });
    } else if (action === DIALOG_TITLE.OTHER_FIELD_CHANGED) {
      setDialogInfo({ open: true, title: "update", actionName: "VERSION_CONFIG_UPDATE", content: `${t("MSG_ConfirmDeletePreviousResult")}\n`, useCustomButton : false });
    }
  }

  const handleClose = (action, selectedPlanVersion, configObj) => {
    if (isEqual(action, "save")) {
      if (!configObj.current?.DP_VERSION_ID) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoSelectedVersion") });
        return;
      } else if (isEqual(configObj.current?.PLANNING_RANGE, "") || !isFinite(configObj.current?.PLANNING_RANGE) || configObj.current?.PLANNING_RANGE <= 0) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_PleaseEnterValues", { values: t("planRange") }) });
        return;
      }

      if (moduleCd === RP) {
        const originConfigObj = selectedPlanVersion?.planConfigObj;
        const changeConfigObj = configObj.current;

        const dpVersionChanged = originConfigObj?.DP_VERSION_ID !== changeConfigObj?.DP_VERSION_ID;
        const configKeys = Object.keys(selectedPlanVersion?.planConfigObj);

        const otherFieldsSame = configKeys.every(key => {
          if (key === "DP_VERSION_ID" || key === "OLD_DP_VERSION") return true;
          if (changeConfigObj[key] === null) return true;
          return originConfigObj[key] === changeConfigObj[key];
        });

        const multiSourcingChanged = (originConfigObj?.MULTI_SOURCING_FLG ?? false) !== (changeConfigObj?.MULTI_SOURCING_FLG ?? false);
        
        selectedPlanVersion.multiSourcingChanged = multiSourcingChanged;

        if (dpVersionChanged && otherFieldsSame && !originConfigObj?.MULTI_SOURCING_FLG) {
          handleDialogOpen(DIALOG_TITLE.ONLY_DPVERSION_CHANGED)
        } else if (multiSourcingChanged) {
          handleDialogOpen(DIALOG_TITLE.MULTI_SOURCING_CHANGED)
        } else {
          handleDialogOpen(DIALOG_TITLE.OTHER_FIELD_CHANGED)
        }
      } else { // MP
        planVersionUpsert();
      }

    } else if (isEqual(action, "cancel")) {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      configObj.current = {};

      const reduxState = reduxUtil.createObjectState(viewName, { sourceVersion: null });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // 우측 패널이 펼쳐지면 모달이 넓어진다 — 프리셋으로는 표현이 안 돼 width 를 직접 계산해 넘긴다
  const sidePanelWidth = expanded ? (isMp ? PANEL_WIDTH.MP : PANEL_WIDTH.RP) : 0;

  return (
    <>
    <BaseConfigModal
      open={modalOpen}
      onClose={() => handleClose("cancel", selectedPlanVersion, configObj)}
      title={t("versionSettings")}
      titleAdornment={
        <IconButton size="small" onClick={() => setExpanded(prev => !prev)} sx={{ ml: 1, color: "cornflowerblue" }} aria-label={expanded ? "collapse" : "expand"}>
          {expanded ? <KeyboardArrowLeftIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        </IconButton>
      }
      /* 릴리스된 버전은 못 고친다 — onSave 를 안 주면 셸이 저장 버튼을 감춘다 */
      onSave={isEqual(selectedPlanVersion?.statusCd, RELEASED) ? undefined : () => handleClose("save", selectedPlanVersion, configObj)}
      width={480 + sidePanelWidth + 32}
      paperSx={{ transition: "width 0.3s ease" }}>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          {/* ── 좌측: 기존 영역 (480px 고정) ── */}
          <Box sx={{ width: 480, flexShrink: 0 }}>
            <VersionConfigContent t={t} viewName={viewName} moduleCd={moduleCd} modalOpen={modalOpen} defaultValue={defaultValue}
              selectedPlanVersion={selectedPlanVersion} dpVersions={dpVersions} rpVersions={rpVersions} configObj={configObj} useModules={useModules}
              planPolicyContainer={isMp ? planPolicyEl : undefined}
              rpPolicyContainer={isRp ? rpPolicyEl : undefined} />
          </Box>

          {/* ── 우측: 확장 패널 (수평 슬라이드) ── */}
          <Box sx={{
              width: sidePanelWidth,
              overflow: "hidden",
              transition: "width 0.3s ease",
              flexShrink: 0,
            }}>
              <Box sx={{ width: isMp ? PANEL_WIDTH.MP : PANEL_WIDTH.RP, pl: 2, overflowY: "auto", maxHeight: "calc(90vh - 100px)", boxSizing: "border-box" }}>
                {isMp
                  ? <Box ref={setPlanPolicyEl} sx={{ width: "100%", boxSizing: "border-box" }} />
                  : isRp
                    ? <Box ref={setRpPolicyEl} sx={{ width: "100%", boxSizing: "border-box" }} />
                    : null
                }
              </Box>
            </Box>
        </Box>
    </BaseConfigModal>
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      actionName={dialogInfo.actionName}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
      el={dialogInfo.el}
      cancelText={dialogInfo.cancelText}
      customButton={dialogInfo.useCustomButton ? <Button size="small" name="custom">{t("cancel")}</Button> : null}
    />
    </>
  );
};

export default withTranslation()(VersionSettingsModal);
